import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { LOCALE } from 'constants/intl-wrapper';
import { customizedFmt } from 'components/IntlWrapper/IntlWrapper';
import { useModal } from 'contexts/modalProvider.context';

import BRRichTextEditor from 'components/BRRichTextEditor/BRRichTextEditor';
import BRBilingualOptions from 'components/BRBilingualOptions/BRBilingualOptions';
import BRButton from 'components/BRButton/BRButton';
import PagesTemplatesModal from 'components/Pages/components/CreateEditPage/components/PagesTemplatesModal/PagesTemplatesModal';

import { ReactComponent as StarsIcon } from 'assets/bosta-icons/stars.svg';

import './PageContentSection.less';

const PageContentSection = ({
  contentData = {},
  setContentData,
  setSelectedTemplateKey,
  defaultLang,
  isEdit,
  setIsFieldsTouched,
  setIsPrimaryBtnDisabled
}) => {
  const { formatMessage } = useIntl();
  const reactQuillRef = useRef();
  const { openModal } = useModal();

  const { EN, AR } = LOCALE;

  const [chosenFieldLang, setChosenFieldLang] = useState(AR);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const onChangeFieldLanguage = ({ value }) => {
    setChosenFieldLang(value);
    reactQuillRef.current.focus();
  };

  const onContentFieldChange = ({ key, value }) => {
    setContentData((prev) => ({ ...prev, [key]: value }));
    setIsFieldsTouched(true);

    if (isEdit) {
      setIsPrimaryBtnDisabled(false);
    }
  };

  const getPropsBasedOnChosenLang = (lang) => {
    const props = {
      [AR]: {
        value: contentData.contentAr,
        placeholder: customizedFmt(AR)({
          id: 'pages.create_edit_page.form.content_field.placeholder'
        }),
        onChange: (value) => onContentFieldChange({ key: 'contentAr', value })
      },
      [EN]: {
        value: contentData.content,
        placeholder: formatMessage({
          id: 'pages.create_edit_page.form.content_field.placeholder'
        }),
        onChange: (value) => onContentFieldChange({ key: 'content', value })
      }
    };

    return props[lang];
  };

  const handleCreateFromTemplate = () => {
    openModal(PagesTemplatesModal, {
      setSelectedTemplateKey
    });
  };

  const handleChangeSelection = (range) => {
    if (isFirstRender && !range?.index) {
      setIsFirstRender(false);
    } else {
      setIsInputFocused(!!range);
    }
  };

  useEffect(() => {
    if (defaultLang) {
      setChosenFieldLang(defaultLang);
    }
  }, [defaultLang]);

  return (
    <div className="br-page-content-section br-create-edit-page-card">
      <div className="br-create-edit-page-card__title">
        {formatMessage({
          id: 'pages.create_edit_page.content_details.title'
        })}
      </div>
      <div className="br-create-edit-page-card__content">
        <div className="br-page-content-section__subheader">
          <BRBilingualOptions
            defaultLang={chosenFieldLang}
            inputValue={{
              [EN]: contentData.content,
              [AR]: contentData.contentAr
            }}
            onChangeFieldLanguage={onChangeFieldLanguage}
            isInputFocused={isInputFocused}
          />
          <BRButton
            prefixIcon={<StarsIcon />}
            label={formatMessage({
              id: 'pages.create_edit_page.create_from_template.create_btn'
            })}
            onClick={handleCreateFromTemplate}
            type="treitary-color"
          />
        </div>
        {chosenFieldLang === EN && (
          <BRRichTextEditor
            reactQuillRef={reactQuillRef}
            handleChangeSelection={handleChangeSelection}
            {...getPropsBasedOnChosenLang(EN)}
          />
        )}
        {chosenFieldLang === AR && (
          <BRRichTextEditor
            reactQuillRef={reactQuillRef}
            handleChangeSelection={handleChangeSelection}
            {...getPropsBasedOnChosenLang(AR)}
          />
        )}
      </div>
    </div>
  );
};

export default PageContentSection;
